/**
 * The stub store: sample data, no server.
 *
 * It holds the sample set, applies a write locally and notifies, which is
 * enough to render every screen. It moved out of `store.ts` when the live store
 * arrived and is otherwise untouched — the seam is the point, and a screen that
 * cannot tell which one it is reading is the proof the seam holds.
 *
 * Construct it in `store.ts` in place of `LiveRelayStore` to get every screen
 * back without a server. The shell reads `.sample` and says so on a strip.
 *
 * The local-write behaviour is not a shortcut of the stub. It is the rule: a
 * write applies locally and renders now, and the ack clears `pending`. A chat
 * client that waits for a round trip feels broken on any connection worse than
 * an office one.
 */

import {
  SAMPLE_CONVERSATIONS, SAMPLE_ME, SAMPLE_MEETINGS, SAMPLE_MESSAGES, SAMPLE_PEOPLE
} from './sample';
import { laneOf, previewOf, threadOf } from './thread';
import type {
  CallState, Conversation, ConversationKind, IncomingCall, Meeting, MeetingInvitee,
  MeetingParticipant, MeetingPolicy, Message, Person, Presence, RelayStore, ThreadView,
  Unsubscribe
} from './types';
import { attachmentOf, type SendWithFiles } from './cloud-picker';

const DEFAULT_POLICY: MeetingPolicy = {
  mic: 'everyone',
  camera: 'everyone',
  screen: 'everyone',
  lobby: 'strangers'
};

let nextId = 1;
const freshId = (prefix: string) => `${prefix}${Date.now().toString(36)}_${nextId++}`;

const NOBODY: MeetingParticipant[] = [];

export class SampleRelayStore implements RelayStore {
  /** True on this implementation and absent from `RelayStore`. The shell reads
   *  it to say so on screen; nothing else may branch on it. */
  readonly sample = true;

  private listeners = new Set<() => void>();
  private incoming = new Set<(call: IncomingCall) => void>();

  private conversationList: Conversation[] = [...SAMPLE_CONVERSATIONS];
  private messageLog: Record<string, Message[]> = structuredClone(SAMPLE_MESSAGES);
  /** What `thread.ts` calls `held`: every id ever in a log, deleted ones too. */
  private deleted = new Set<string>();
  private meetingList: Meeting[] = [...SAMPLE_MEETINGS];
  private peopleById = new Map(SAMPLE_PEOPLE.map((p) => [p.id, p]));
  private typingBy: Record<string, string[]> = { con_design: ['usr_rahul'] };
  private calls: Record<string, CallState> = {};

  /**
   * Derived answers, held until something changes.
   *
   * Not an optimisation. `useStore` is `useSyncExternalStore`, which compares
   * the selected value with `Object.is` and warns — then loops — when a getter
   * returns a freshly built array every time it is asked. Every getter that
   * filters or maps therefore has to return the *same* array until the thing it
   * was derived from changes, and this is where that is kept.
   */
  private derived = new Map<string, unknown>();

  private memo<T>(key: string, build: () => T): T {
    if (!this.derived.has(key)) this.derived.set(key, build());
    return this.derived.get(key) as T;
  }

  private changed(): void {
    this.derived.clear();
    for (const listener of this.listeners) listener();
  }

  subscribe(listener: () => void): Unsubscribe {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ── reads ─────────────────────────────────────────────────────────────────

  me(): Person | null {
    return SAMPLE_ME;
  }

  conversations(): Conversation[] {
    return this.conversationList;
  }

  /** The transcript lane, by the live store's rules (`thread.ts`). */
  messages(conversationId: string): Message[] {
    return this.memo(`messages:${conversationId}`, () =>
      laneOf(this.messageLog[conversationId] ?? [], this.held(conversationId))
    );
  }

  thread(conversationId: string, rootMessageId: string): ThreadView {
    return this.memo(`thread:${conversationId}:${rootMessageId}`, () =>
      threadOf(this.messageLog[conversationId] ?? [], this.held(conversationId), rootMessageId)
    );
  }

  newestSeq(conversationId: string): number {
    const log = this.messageLog[conversationId] ?? [];
    return log.reduce((max, m) => Math.max(max, m.seq), 0);
  }

  private held(conversationId: string): Set<string> {
    const ids = new Set(this.deleted);
    for (const m of this.messageLog[conversationId] ?? []) ids.add(m.id);
    return ids;
  }

  people(ids: string[]): Person[] {
    return this.memo(`people:${ids.join(',')}`, () => {
      const out: Person[] = [];
      for (const id of ids) {
        const person = this.peopleById.get(id);
        if (person) out.push(person);
      }
      return out;
    });
  }

  presenceOf(userId: string): Presence {
    return this.peopleById.get(userId)?.presence ?? 'offline';
  }

  typing(conversationId: string): Person[] {
    return this.people((this.typingBy[conversationId] ?? []).filter((id) => id !== SAMPLE_ME.id));
  }

  callState(conversationId: string): CallState | null {
    return this.calls[conversationId] ?? null;
  }

  meetings(): Meeting[] {
    return this.meetingList;
  }

  hasOlder(): boolean {
    // The sample log is the whole history; there is nothing below it.
    return false;
  }

  // ── writes ────────────────────────────────────────────────────────────────

  async loadOlder(): Promise<void> {
    // Nothing to fetch, for the reason `hasOlder` gives.
  }

  send(conversationId: string, body: string, opts?: SendWithFiles): string {
    const existing = this.messageLog[conversationId] ?? [];
    const id = freshId('m_');
    const message: Message = {
      id,
      conversationId,
      seq: (existing[existing.length - 1]?.seq ?? 0) + 1,
      senderId: SAMPLE_ME.id,
      at: Date.now(),
      body,
      attachments: (opts?.files ?? []).map((f) => attachmentOf(f)),
      kind: 'msg',
      pending: 'sending',
      ...(opts?.replyTo ? { replyTo: opts.replyTo } : {}),
      ...(opts?.mentions?.length ? { mentions: opts.mentions } : {})
    };

    this.messageLog[conversationId] = [...existing, message];
    this.touch(conversationId, message);
    this.changed();

    // What the ack would do. The real store does this on the `ack` frame, under
    // the same message id — the id is the server's dedup key and a retry reuses
    // it.
    setTimeout(() => {
      const log = this.messageLog[conversationId];
      if (!log) return;
      this.messageLog[conversationId] = log.map((m) =>
        m.id === id ? { ...m, pending: undefined } : m
      );
      this.changed();
    }, 400);

    return id;
  }

  /** A reply moves the row and leaves its line alone, as the live store's. */
  private touch(conversationId: string, message: Message): void {
    const shown = previewOf(this.messageLog[conversationId] ?? []);
    this.conversationList = this.conversationList.map((c) =>
      c.id === conversationId ? { ...c, lastAt: message.at, preview: shown?.body ?? '' } : c
    );
  }

  setTyping(): void {
    // The ephemeral lane. Nothing local to change.
  }

  setPresence(presence: Presence): void {
    this.peopleById.set(SAMPLE_ME.id, { ...SAMPLE_ME, presence });
    this.changed();
  }

  async lookup(handleOrEmail: string): Promise<Person | null> {
    const needle = handleOrEmail.trim().toLowerCase().replace(/^@/, '');
    for (const person of this.peopleById.values()) {
      if (person.handle?.toLowerCase() === needle) return person;
    }
    return null;
  }

  async createConversation(
    kind: Exclude<ConversationKind, 'self'>,
    title: string,
    memberIds: string[]
  ): Promise<string> {
    const id = freshId('con_');
    this.conversationList = [
      {
        id,
        kind,
        title,
        topic: '',
        members: [SAMPLE_ME.id, ...memberIds],
        ownerId: SAMPLE_ME.id,
        unread: 0,
        lastAt: Date.now(),
        preview: ''
      },
      ...this.conversationList
    ];
    this.messageLog[id] = [];
    this.changed();
    return id;
  }

  async addMember(conversationId: string, userId: string): Promise<void> {
    this.patchConversation(conversationId, (c) => ({
      ...c,
      members: c.members.includes(userId) ? c.members : [...c.members, userId]
    }));
  }

  async removeMember(conversationId: string, userId: string): Promise<void> {
    this.patchConversation(conversationId, (c) => ({
      ...c,
      members: c.members.filter((m) => m !== userId)
    }));
  }

  async leave(conversationId: string): Promise<void> {
    this.conversationList = this.conversationList.filter((c) => c.id !== conversationId);
    this.changed();
  }

  async deleteForEveryone(conversationId: string): Promise<void> {
    this.conversationList = this.conversationList.filter((c) => c.id !== conversationId);
    this.changed();
  }

  async rename(conversationId: string, title: string, topic?: string): Promise<void> {
    this.patchConversation(conversationId, (c) => ({
      ...c,
      title,
      topic: topic ?? c.topic
    }));
  }

  private patchConversation(id: string, patch: (c: Conversation) => Conversation): void {
    this.conversationList = this.conversationList.map((c) => (c.id === id ? patch(c) : c));
    this.changed();
  }

  markRead(conversationId: string): void {
    this.patchConversation(conversationId, (c) => ({ ...c, unread: 0, mentioned: false }));
  }

  editMessage(conversationId: string, messageId: string, body: string): void {
    const text = body.trim();
    if (!text) return;
    this.rewrite(conversationId, (log) =>
      log.map((m) => (m.id === messageId && m.senderId === SAMPLE_ME.id ? { ...m, body: text } : m))
    );
  }

  deleteMessage(conversationId: string, messageId: string): void {
    const target = this.messageLog[conversationId]?.find((m) => m.id === messageId);
    if (target?.senderId !== SAMPLE_ME.id) return;
    this.deleted.add(messageId);
    this.rewrite(conversationId, (log) => log.filter((m) => m.id !== messageId));
  }

  private rewrite(conversationId: string, change: (log: Message[]) => Message[]): void {
    this.messageLog[conversationId] = change(this.messageLog[conversationId] ?? []);
    const shown = previewOf(this.messageLog[conversationId]);
    this.conversationList = this.conversationList.map((c) =>
      c.id === conversationId ? { ...c, preview: shown?.body ?? '' } : c
    );
    this.changed();
  }

  // ── meetings ──────────────────────────────────────────────────────────────

  async createMeeting(opts: {
    title: string;
    scheduledAt?: number;
    conversationId?: string;
    invitees?: MeetingInvitee[];
    policy?: Partial<MeetingPolicy>;
  }): Promise<Meeting> {
    const meeting: Meeting = {
      id: freshId('mtg_'),
      ownerId: SAMPLE_ME.id,
      title: opts.title,
      scheduledAt: opts.scheduledAt ?? 0,
      status: 'scheduled',
      invitees: opts.invitees ?? [],
      conversationId: opts.conversationId ?? '',
      policy: { ...DEFAULT_POLICY, ...opts.policy },
      recording: false,
      own: true
    };
    this.meetingList = [meeting, ...this.meetingList];
    this.changed();
    return meeting;
  }

  async endMeeting(meetingId: string): Promise<void> {
    this.patchMeeting(meetingId, 'ended');
  }

  async cancelMeeting(meetingId: string): Promise<void> {
    this.patchMeeting(meetingId, 'cancelled');
  }

  private patchMeeting(id: string, status: Meeting['status']): void {
    this.meetingList = this.meetingList.map((m) => (m.id === id ? { ...m, status } : m));
    this.changed();
  }

  async joinLink(meetingId: string): Promise<string> {
    return `${window.location.origin}/m/sample-token-${meetingId}`;
  }

  /** Nobody knocks at a sample meeting. The same array every time, for the
   *  reason `derived` above exists. */
  lobby(): MeetingParticipant[] {
    return NOBODY;
  }

  async admit(): Promise<void> {}

  async deny(): Promise<void> {}

  // ── ringing ───────────────────────────────────────────────────────────────

  onIncomingCall(cb: (call: IncomingCall) => void): Unsubscribe {
    this.incoming.add(cb);
    return () => this.incoming.delete(cb);
  }

  ring(conversationId: string, callId: string): void {
    this.calls[conversationId] = {
      conversationId,
      callId,
      since: Date.now(),
      roster: [{ userId: SAMPLE_ME.id, deviceId: 'dev_web', joinedAt: Date.now() }]
    };
    this.changed();
  }

  answer(): void {}

  decline(): void {}

  hangUp(conversationId: string): void {
    delete this.calls[conversationId];
    this.changed();
  }

  // ── The live store's own five ─────────────────────────────────────────────
  //
  // `adopt`, `boot`, `connection`, `onCallFrames`, `postCallEnd` and
  // `joinByToken` are not on `RelayStore` — the composition root calls them on
  // the live store directly. They are answered here so that `?sample=1` can
  // hand this object to the same root without it having to ask which store it
  // holds. Each does the honest nothing: there is no session to adopt, no
  // socket to report on and nowhere to post a call record to.

  adopt(): void {}

  async boot(): Promise<void> {}

  /** "Connected", because the strip that reads this is drawing sample data and
   *  a reconnecting spinner over it would be a second fiction. The word is the
   *  socket's own: `SocketState` is `'online'`, and answering `'open'` here is
   *  what let the live strip's wrong comparison look right offline. */
  connection(): 'online' {
    return 'online';
  }

  onCallFrames(): Unsubscribe {
    return () => {};
  }

  postCallEnd(): void {}

  async joinByToken(): Promise<never> {
    throw new Error('sample store: there is no meeting to join');
  }
}

